import CookieBanner from '@components/common/CookieBanner';
import { CLARITY_PROJECT_ID } from '@constants/env';
import { useOnce } from '@conversed/ui/src/hooks/useOnce';
import { useMainSelector } from '@hooks/useMainSelector';
import Clarity from '@microsoft/clarity';
import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import type { Socket } from 'socket.io-client';
import {
  LOGIN_PAGE_ROUTE,
  MAIN_PAGE_ROUTE,
  NEW_CUSTOMER_ROUTE,
  PROVISION_STATUS_ROUTE,
  SELECT_CUSTOMER_ROUTE,
  TAILWIND_PAGE_COMPONENTS,
} from '../../constants/routes';
import { useChatNotificationsTitle } from '../../hooks/chat/notifications/useChatNotificationsTitle';
import { useCompanySelector } from '../../hooks/useCompanySelector';
import { useCurrentUserInit } from '../../hooks/user/useCurrentUserInit';
import useSocketEvents from '../../hooks/useSocketEvents';
import { TopdeskArticleGenerationProvider } from '../article-architect/TopdeskArticleGenerationProvider';
import { CompanySelectorPage } from '../pages/CompanySelectorPage';
import { NewCustomerPage } from '../pages/NewCustomerPage';
import { ProvisioningStatusPage } from '../pages/ProvisioningStatusPage';
import { TailwindDemoPage } from '../pages/TailwindDemoPage';
import { AuthenticatedRouter } from '../routers/AuthenticatedRouter';
import { AppBar } from './header/AppBar';
import { AppSidebar } from './sidebar/AppSidebar';

export const WebSocketContext =
  React.createContext<React.RefObject<Socket | null> | null>(null);

export const AuthenticatedLayout: React.FC = () => {
  const { loading, error } = useCurrentUserInit();
  const { socketRef } = useSocketEvents();
  const { selectedCompany } = useCompanySelector();
  const {
    user: { currentUser },
  } = useMainSelector();
  const isSuperAdmin = currentUser?.isSuperAdmin ?? false;

  // * Init title change on notification count
  useChatNotificationsTitle();

  useOnce(() => {
    // * Run Clarity only if a project ID is added
    if (CLARITY_PROJECT_ID) {
      Clarity.init(CLARITY_PROJECT_ID);
    }
  });

  const [hasIdentified, setHasIdentified] = useState(false);

  useEffect(() => {
    // * Identify once per session, let Clarity handle page tracking automatically
    if (loading || error || !currentUser?.email || hasIdentified) {
      return;
    }

    const { email, firstName, lastName } = currentUser;
    const friendlyName = `${firstName || ''} ${lastName || ''}`.trim() || email;

    if (CLARITY_PROJECT_ID && email) {
      // Starting October 31, 2025, Microsoft Clarity will require websites to obtain explicit user consent for users
      // from the European Economic Area (EEA), the United Kingdom (UK), and Switzerland (CH) before collecting any data via cookies.
      // Since cookie banner states "By using this page, you consent to our use of cookies"
      // we can send consent signal based on the fact that user is actively using the application.
      Clarity.consent(true);

      Clarity.identify(email, undefined, undefined, friendlyName);
      setHasIdentified(true);
    }
  }, [error, loading, currentUser, hasIdentified]);

  if (loading || error) {
    return null;
  }

  return (
    <Routes>
      {isSuperAdmin && (
        <>
          <Route path={NEW_CUSTOMER_ROUTE} element={<NewCustomerPage />} />
          <Route
            path={PROVISION_STATUS_ROUTE}
            element={<ProvisioningStatusPage />}
          />
        </>
      )}
      <Route path={TAILWIND_PAGE_COMPONENTS} element={<TailwindDemoPage />} />

      <Route path={SELECT_CUSTOMER_ROUTE} element={<CompanySelectorPage />} />

      <Route
        path={LOGIN_PAGE_ROUTE}
        element={<Navigate to={MAIN_PAGE_ROUTE} />}
      />

      <Route
        path='*'
        element={
          selectedCompany ? (
            <WebSocketContext.Provider value={socketRef}>
              {/* Sidebar is the full-height left column; the top bar and content
               * sit in the column to its right. On mobile the sidebar floats as
               * an overlay (see AppSidebar) so the top bar spans the full width. */}
              <div
                data-testid='app-shell'
                className='flex flex-row h-[100dvh] min-w-[375px] relative'>
                <AppSidebar />
                <div className='flex flex-col flex-1 min-w-0'>
                  <AppBar />
                  <div
                    id='main-scroll-container'
                    className='flex-1 overflow-y-auto pr-0 z-[1]'>
                    <TopdeskArticleGenerationProvider>
                      <AuthenticatedRouter />
                    </TopdeskArticleGenerationProvider>
                  </div>
                </div>
                <CookieBanner />
              </div>
            </WebSocketContext.Provider>
          ) : (
            <Navigate to={SELECT_CUSTOMER_ROUTE} />
          )
        }
      />
    </Routes>
  );
};
